// Living Atlas data hooks. One shared TanStack query per key (any number of
// screens share a request), seeded from localStorage snapshots like the rest
// of Kinmantle. Each hook only runs while its mode is mounted, so leaving a
// mode stops its polling.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_ATLAS_SETTINGS, type AtlasSettings } from "../../shared/atlas/settings";
import type {
  AirAtlasPayload,
  AtlasStatusPayload,
  AuroraAtlasPayload,
  FireIncidentsPayload,
  FiresAtlasPayload,
  FireWindow,
  LiveEarthPayload,
  QuakesAtlasPayload,
  QuakeWindow,
  RiversAtlasPayload,
  SmokeAtlasPayload,
  SnowAtlasPayload,
  StormAlertsPayload,
  StormReportDay,
  StormReportsPayload,
  TropicalPayload,
} from "../../shared/atlas/types";
import type { OceanAtlasPayload, PlanetEventsPayload, ReservoirsAtlasPayload, SnowAnalysisPayload, VolcanoesPayload } from "../../shared/atlas/types";
import { api } from "../data/api";
import { useSkyQuery } from "../widgets/sky/api";

const MIN = 60_000;

export function useAtlasSettings() {
  const q = useQuery({ queryKey: ["atlas", "settings"], queryFn: ({ signal }) => api<AtlasSettings>("/atlas/settings", { signal }), staleTime: 2 * MIN, refetchInterval: 5 * MIN });
  return { ...q, settings: q.data ?? DEFAULT_ATLAS_SETTINGS };
}

export function useUpdateAtlasSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<AtlasSettings>) => api<AtlasSettings>("/atlas/settings", { method: "PATCH", body: patch }),
    onSuccess: (s) => qc.setQueryData(["atlas", "settings"], s),
  });
}

export function useAtlasStatus(enabled = true) {
  return useQuery({ queryKey: ["atlas", "status"], queryFn: ({ signal }) => api<AtlasStatusPayload>("/atlas/status", { signal }), refetchInterval: 5 * MIN, staleTime: MIN, enabled });
}

const POLL: Record<QuakeWindow, number> = { hour: 2 * MIN, day: 5 * MIN, week: 10 * MIN, month: 15 * MIN };

export function useAtlasQuakes(window: QuakeWindow) {
  return useSkyQuery<QuakesAtlasPayload>(["atlas", "quakes", window], `/atlas/earthpulse?window=${window}`, { refetchMs: POLL[window], staleMs: POLL[window] / 2 });
}

export function useAtlasAurora() {
  return useSkyQuery<AuroraAtlasPayload>(["atlas", "aurora"], "/atlas/aurora", { refetchMs: 10 * MIN, staleMs: 5 * MIN });
}

export function useLiveEarth() {
  return useSkyQuery<LiveEarthPayload>(["atlas", "live-earth"], "/atlas/live-earth", { refetchMs: 10 * MIN, staleMs: 5 * MIN });
}

export function useAtlasFires(window: FireWindow, region: "us" | "north-america") {
  return useSkyQuery<FiresAtlasPayload>(["atlas", "fires", window, region], `/atlas/firewatch?window=${window}&region=${region}`, { refetchMs: 20 * MIN, staleMs: 10 * MIN });
}

export function useFireIncidents(enabled: boolean) {
  return useSkyQuery<FireIncidentsPayload>(["atlas", "fire-incidents"], "/atlas/fire-incidents", { refetchMs: 15 * MIN, staleMs: 10 * MIN, enabled });
}

export function useAtlasRivers() {
  return useSkyQuery<RiversAtlasPayload>(["atlas", "rivers"], "/atlas/rivers", { refetchMs: 15 * MIN, staleMs: 10 * MIN });
}

export function useAtlasReservoirs(enabled: boolean) {
  return useSkyQuery<ReservoirsAtlasPayload>(["atlas", "reservoirs"], "/atlas/reservoirs", { refetchMs: 30 * MIN, staleMs: 20 * MIN, enabled });
}

/** `state` null lets the Worker choose (the home state when SNOTEL covers it, else Colorado). */
export function useAtlasSnow(state: string | null) {
  return useSkyQuery<SnowAtlasPayload>(["atlas", "snow", state ?? "default"], state ? `/atlas/snow?state=${state}` : "/atlas/snow", { refetchMs: 60 * MIN, staleMs: 30 * MIN });
}

export function useAtlasAir() {
  return useSkyQuery<AirAtlasPayload>(["atlas", "air"], "/atlas/air", { refetchMs: 20 * MIN, staleMs: 10 * MIN });
}

/** `date` null = the latest HMS analysis. */
export function useAtlasSmoke(date: string | null, enabled = true) {
  return useSkyQuery<SmokeAtlasPayload>(["atlas", "smoke", date ?? "latest"], date ? `/atlas/smoke?date=${date}` : "/atlas/smoke", { refetchMs: 20 * MIN, staleMs: 10 * MIN, enabled });
}

export function useStormAlerts(enabled: boolean) {
  return useSkyQuery<StormAlertsPayload>(["atlas", "storm-alerts"], "/atlas/storm-alerts", { refetchMs: 3 * MIN, staleMs: 2 * MIN, enabled });
}

export function useStormReports(day: StormReportDay, enabled: boolean) {
  return useSkyQuery<StormReportsPayload>(["atlas", "storm-reports", day], `/atlas/storm-reports?day=${day}`, { refetchMs: day === "today" ? 10 * MIN : 60 * MIN, staleMs: 5 * MIN, enabled });
}

export function useTropical(enabled: boolean) {
  return useSkyQuery<TropicalPayload>(["atlas", "tropical"], "/atlas/tropical", { refetchMs: 15 * MIN, staleMs: 10 * MIN, enabled });
}

// ---------- Phase 3 ----------

export function usePlanetEvents() {
  return useSkyQuery<PlanetEventsPayload>(["atlas", "planet-events"], "/atlas/planet-events", { refetchMs: 30 * MIN, staleMs: 15 * MIN });
}

export function useUsVolcanoes(enabled: boolean) {
  return useSkyQuery<VolcanoesPayload>(["atlas", "volcanoes"], "/atlas/volcanoes", { refetchMs: 15 * MIN, staleMs: 10 * MIN, enabled });
}

export function useSnowAnalysis(enabled: boolean) {
  return useSkyQuery<SnowAnalysisPayload>(["atlas", "snow-analysis"], "/atlas/snow-analysis", { refetchMs: 60 * MIN, staleMs: 30 * MIN, enabled });
}

export function useOcean() {
  return useSkyQuery<OceanAtlasPayload>(["atlas", "ocean"], "/atlas/ocean", { refetchMs: 60 * MIN, staleMs: 30 * MIN });
}
